import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { getAuthFeatures, requestMagicLink } from "../api/client";

type LocationState = { from?: { pathname?: string } } | null;

export function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [magicLinkEnabled, setMagicLinkEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    getAuthFeatures()
      .then((f) => setMagicLinkEnabled(f.magic_link_enabled))
      .catch(() => setMagicLinkEnabled(false));
  }, []);

  const redirectTo = useMemo(() => {
    const st = (location.state as LocationState) ?? null;
    return st?.from?.pathname && typeof st.from.pathname === "string" ? st.from.pathname : "/home";
  }, [location.state]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.loginWithPassword({ email: email.trim(), password });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSendLink(e: React.FormEvent) {
    e.preventDefault();
    setLinkError(null);
    setSendingLink(true);
    try {
      await requestMagicLink(magicLinkEmail.trim());
      navigate(`/auth/magic-link/sent?email=${encodeURIComponent(magicLinkEmail.trim())}`, { replace: true });
    } catch (err) {
      // If magic links aren't enabled, backend likely returns 404 -> show a friendly message.
      setLinkError(err instanceof Error ? err.message : "Failed to send sign-in link");
    } finally {
      setSendingLink(false);
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Sign in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Use your OpenMon account to continue.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          <Stack spacing={2}>
            <Stack component="form" spacing={2} onSubmit={onSubmit}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                disabled={submitting || auth.status === "loading"}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                disabled={submitting || auth.status === "loading"}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || auth.status !== "unauthenticated"}
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>

            {magicLinkEnabled ? (
              <>
                <Divider />

                <Stack component="form" spacing={1.5} onSubmit={onSendLink}>
                  {linkError ? <Alert severity="error">{linkError}</Alert> : null}
                  <Typography variant="subtitle2">Sign in with email</Typography>
                  <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    value={magicLinkEmail}
                    onChange={(e) => setMagicLinkEmail(e.target.value)}
                    fullWidth
                    disabled={sendingLink || auth.status === "loading"}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={sendingLink || auth.status !== "unauthenticated" || magicLinkEmail.trim().length === 0}
                  >
                    {sendingLink ? "Sending…" : "Send sign-in link"}
                  </Button>
                </Stack>

                <Divider />
              </>
            ) : null}

            <Typography variant="body2" color="text.secondary">
              Don’t have an account?{" "}
              <Link component={RouterLink} to="/register" underline="hover">
                Create one
              </Link>
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

